import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userLabel, type UserDef } from '@system-atlas/shared';
import { Button, TextInput } from '../components/ui';
import { cn } from '../lib/utils';

interface UserDirectoryManagerProps {
  users: UserDef[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onOpenImport: () => void;
  onClose: () => void;
}

/** Verwaltung des Benutzer-Verzeichnisses (Baustein G): ansehen, suchen, loeschen, leeren, importieren. */
export function UserDirectoryManager({ users, onRemove, onClear, onOpenImport, onClose }: UserDirectoryManagerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered =
    q === ''
      ? users
      : users.filter(
          (u) =>
            u.displayName.toLowerCase().includes(q) ||
            (u.upn ?? '').toLowerCase().includes(q) ||
            (u.department ?? '').toLowerCase().includes(q),
        );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('editor.userDirectory.title')}
      onMouseDown={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('editor.userDirectory.title')}</h2>
          <Button variant="ghost" className="px-2" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </Button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <TextInput
            className="flex-1"
            value={query}
            placeholder={t('editor.userDirectory.search')}
            aria-label={t('editor.userDirectory.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button variant="secondary" onClick={onOpenImport}>
            {t('editor.userDirectory.import')}
          </Button>
        </div>

        {users.length === 0 ? (
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">{t('editor.userDirectory.empty')}</p>
        ) : (
          <ul className="mb-4 divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((u) => (
              <li key={u.id} className="flex items-center gap-2 py-1.5">
                <span
                  className={cn('h-2 w-2 shrink-0 rounded-full', u.enabled === false ? 'bg-slate-300 dark:bg-slate-600' : 'bg-emerald-500')}
                  title={u.enabled === false ? t('editor.userDirectory.disabled') : t('editor.userDirectory.enabled')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{userLabel(u)}</span>
                  {u.upn && <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{u.upn}</span>}
                </span>
                <Button variant="danger" className="px-2 py-1 text-xs" onClick={() => onRemove(u.id)}>
                  {t('editor.delete')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {users.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-200 pt-4 dark:border-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('editor.userDirectory.count', { count: users.length })}</span>
            <Button variant="danger" className="px-2 py-1 text-xs" onClick={onClear}>
              {t('editor.userDirectory.clear')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
